import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  InputAdornment,
  Grid,
  Container,
} from '@mui/material';
import {
  Add as AddIcon,
  Create as CreateIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';

import { getSuggestions as apiGetSuggestions, updateSuggestion as apiUpdateSuggestion, deleteSuggestion as apiDeleteSuggestion, Suggestion } from '../services/api';

const Suggestions: React.FC = () => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);
  const [responseText, setResponseText] = useState('');
  const [responding, setResponding] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [suggestionToDelete, setSuggestionToDelete] = useState<Suggestion | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'responded'>('all');

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      const res = await apiGetSuggestions();
      const data = res.data;
      setSuggestions(data.data || data);
      setFilteredSuggestions(data.data || data);
    } catch (error) {
      console.error('Error loading suggestions:', error);
      showSnackbarMessage('Failed to load suggestions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, []);

  useEffect(() => {
    let filtered = suggestions;

    // Filter by status
    if (filterStatus === 'pending') {
      filtered = filtered.filter(s => !s.response);
    } else if (filterStatus === 'responded') {
      filtered = filtered.filter(s => s.response);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.suggestion.toLowerCase().includes(term) ||
        s.response?.toLowerCase().includes(term) ||
        s.creator.toLowerCase().includes(term) ||
        `${s.creator_fname} ${s.creator_lname}`.toLowerCase().includes(term)
      );
    }

    setFilteredSuggestions(filtered);
  }, [suggestions, searchTerm, filterStatus]);

  const handleRespond = async () => {
    if (!selectedSuggestion || !responseText.trim()) {
      showSnackbarMessage('Please enter a response', 'error');
      return;
    }

    try {
      setResponding(true);
      await apiUpdateSuggestion((selectedSuggestion.id as number), { response: responseText.trim() });

      setResponseText('');
      setShowResponseDialog(false);
      setSelectedSuggestion(null);
      await loadSuggestions();
      showSnackbarMessage('Response sent successfully', 'success');
    } catch (error) {
      console.error('Error responding to suggestion:', error);
      showSnackbarMessage('Failed to send response', 'error');
    } finally {
      setResponding(false);
    }
  };

  const handleDeleteSuggestion = async () => {
    if (!suggestionToDelete) return;

    try {
      await apiDeleteSuggestion(suggestionToDelete.id as number);
      // ^ Type narrowing ensures suggestionToDelete is not null; id is cast to number for TS safety

      await loadSuggestions();
      showSnackbarMessage('Suggestion deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting suggestion:', error);
      showSnackbarMessage('Failed to delete suggestion', 'error');
    } finally {
      setSuggestionToDelete(null);
      setShowDeleteDialog(false);
    }
  };

  const showSnackbarMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setShowSnackbar(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusChip = (suggestion: Suggestion) => {
    if (suggestion.response) {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Responded"
          color="success"
          size="small"
        />
      );
    }
    return (
      <Chip
        icon={<ScheduleIcon />}
        label="Pending"
        color="warning"
        size="small"
      />
    );
  };

  const getCreatorName = (suggestion: Suggestion) => {
    if (suggestion.creator_fname && suggestion.creator_lname) {
      return `${suggestion.creator_fname} ${suggestion.creator_lname}`;
    }
    return suggestion.creator;
  };

  const getResponderName = (suggestion: Suggestion) => {
    if (suggestion.responder_fname && suggestion.responder_lname) {
      return `${suggestion.responder_fname} ${suggestion.responder_lname}`;
    }
    return suggestion.responder;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" gutterBottom>
          Suggestions Management
        </Typography>

        {/* Search and Filter */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search suggestions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          <Tabs
            value={filterStatus}
            onChange={(_, newValue) => setFilterStatus(newValue)}
            variant="fullWidth"
          >
            <Tab label={`All (${suggestions.length})`} value="all" />
            <Tab label={`Pending (${suggestions.filter(s => !s.response).length})`} value="pending" />
            <Tab label={`Responded (${suggestions.filter(s => s.response).length})`} value="responded" />
          </Tabs>
        </Paper>

        {/* Results Count */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {filteredSuggestions.length} suggestion{filteredSuggestions.length !== 1 ? 's' : ''} found
        </Typography>

                 {/* Suggestions List */}
         {filteredSuggestions.length > 0 ? (
           <Box>
             {filteredSuggestions.map((suggestion) => (
               <Box key={suggestion.id} sx={{ mb: 2 }}>
                <Card>
                  <CardHeader
                    title={
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                          <CreateIcon sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            Suggestion #{suggestion.id}
                          </Typography>
                        </Box>
                        {getStatusChip(suggestion)}
                      </Box>
                    }
                    subheader={`From: ${getCreatorName(suggestion)}`}
                  />
                  <CardContent>
                    <Typography variant="body1" paragraph>
                      <strong>Suggestion:</strong>
                    </Typography>
                    <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
                      {suggestion.suggestion}
                    </Typography>

                    {suggestion.response && (
                      <>
                        <Typography variant="body1" paragraph>
                          <strong>Response:</strong>
                        </Typography>
                        <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap', color: 'success.main' }}>
                          {suggestion.response}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          By: {getResponderName(suggestion)}
                        </Typography>
                      </>
                    )}

                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                      <Box>
                        <Chip
                          icon={<ScheduleIcon />}
                          label={formatDate(suggestion.created_at)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        
                        {suggestion.responded_at && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label={`Responded ${formatDate(suggestion.responded_at)}`}
                            color="success"
                            size="small"
                          />
                        )}
                      </Box>

                      <Box>
                        {!suggestion.response && (
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<ReplyIcon />}
                            onClick={() => {
                              setSelectedSuggestion(suggestion);
                              setShowResponseDialog(true);
                            }}
                            sx={{ mr: 1 }}
                          >
                            Respond
                          </Button>
                        )}
                        
                        <IconButton
                          color="error"
                          onClick={() => {
                            setSuggestionToDelete(suggestion);
                            setShowDeleteDialog(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                                     </CardContent>
                 </Card>
               </Box>
             ))}
           </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CreateIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No suggestions found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No suggestions match your current filters
            </Typography>
          </Paper>
        )}

        {/* Response Dialog */}
        <Dialog
          open={showResponseDialog}
          onClose={() => setShowResponseDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Respond to Suggestion</DialogTitle>
          <DialogContent>
            {selectedSuggestion && (
              <Box mb={2}>
                <Typography variant="h6">Original Suggestion:</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                  {selectedSuggestion.suggestion}
                </Typography>
              </Box>
            )}
            
            <TextField
              fullWidth
              multiline
              rows={6}
              variant="outlined"
              label="Your Response"
              value={responseText}
              onChange={(e) => setResponseText(e.target.value)}
              placeholder="Enter your response..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowResponseDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRespond}
              variant="contained"
              disabled={responding || !responseText.trim()}
            >
              {responding ? 'Sending...' : 'Send Response'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
        >
          <DialogTitle>Delete Suggestion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this suggestion? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteSuggestion} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowSnackbar(false)}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Suggestions;
